import { useState } from 'react'

const VS_PAR_COLOR = (vp) => vp < -1 ? '#C19A6B' : vp === -1 ? '#00563F' : vp === 0 ? '#1A1814' : vp === 1 ? '#C0392B' : '#8B0000'

export default function ScorecardMatrix({ match, players, profiles, scorecards, fullScorecards, highlightPlayer, onEdit, onKick, matchPlayers, golfProfiles }) {
  const [view, setView] = useState('front')

  const holePars = match?.hole_pars || []
  const numHoles = match?.num_holes || 18
  const half = Math.floor(numHoles / 2)

  const holeRange = view === 'front'
    ? Array.from({ length: half }, (_, i) => i)
    : Array.from({ length: numHoles - half }, (_, i) => i + half)

  const outPar = holePars.slice(0, half).reduce((a, b) => a + b, 0)
  const inPar = holePars.slice(half).reduce((a, b) => a + b, 0)
  const totalPar = outPar + inPar

  const getScore = (pid, holeIdx) => (scorecards[pid] || [])[holeIdx] ?? null
  const getTotal = (pid, from, to) => (scorecards[pid] || []).slice(from, to).reduce((a, b) => b != null ? a + b : a, 0)
  const getStatus = (pid) => fullScorecards?.[pid]?.status || null

  const getReviewHoursLeft = (pid) => {
    const sc = fullScorecards?.[pid]
    if (!sc?.review_started_at) return null
    const elapsed = Date.now() - new Date(sc.review_started_at).getTime()
    const remaining = 24 * 60 * 60 * 1000 - elapsed
    return remaining > 0 ? Math.ceil(remaining / 3600000) : 0
  }

  const statusTag = (pid) => {
    const status = getStatus(pid)
    if (!status || status === 'live') return { label: '● LIVE', color: '#00563F' }
    if (status === 'review') { const h = getReviewHoursLeft(pid); return { label: `⏳ REVIEW ${h}h`, color: '#C19A6B' } }
    if (status === 'closed') return { label: '✓ CLOSED', color: '#7A7570' }
    return null
  }

  const allAgreed = players.every(p => {
    const sc = fullScorecards?.[p]
    return sc && (sc.agreed_by || []).includes(p)
  })

  const sortedPlayers = [...players].sort((a, b) => {
    const scA = scorecards[a] || []
    const scB = scorecards[b] || []
    const totalA = scA.reduce((acc, s) => s != null ? acc + s : acc, 0)
    const totalB = scB.reduce((acc, s) => s != null ? acc + s : acc, 0)
    const playedA = scA.filter(s => s != null).length
    const playedB = scB.filter(s => s != null).length
    if (playedA === 0 && playedB === 0) return 0
    if (playedA === 0) return 1
    if (playedB === 0) return -1
    return totalA - totalB
  })

  const getHcpBadge = (pid) => {
    const gp = golfProfiles?.[pid]
    if (!gp) return null
    const color = gp.hcp_type === 'ghin' ? '#00563F' : gp.hcp_type === 'crossbet' ? '#C19A6B' : '#7A7570'
    const label = gp.hcp_type === 'ghin' ? 'GHIN' : gp.hcp_type === 'crossbet' ? 'CBT' : 'MNL'
    const hcp = gp.handicap_index != null ? gp.handicap_index : '—'
    return { color, label, hcp }
  }

  return (
    <div style={s.wrap}>
      {/* F9 / B9 toggle */}
      <div style={s.toggle}>
        <button onClick={() => setView('front')} style={{ ...s.toggleBtn, ...(view === 'front' ? s.toggleActive : {}) }}>
          Front {half}
        </button>
        <button onClick={() => setView('back')} style={{ ...s.toggleBtn, ...(view === 'back' ? s.toggleActive : {}) }}>
          Back {numHoles - half}
        </button>
      </div>

      {/* Matrix */}
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.nameCol}></th>
              {holeRange.map(i => <th key={i} style={s.holeCol}>{i + 1}</th>)}
              <th style={s.totalCol}>{view === 'front' ? 'OUT' : 'IN'}</th>
              <th style={s.totalCol}>TOT</th>
              <th style={s.totalCol}>+/-</th>
            </tr>
            <tr style={s.parRowStyle}>
              <td style={{ ...s.nameCol, color: '#7A7570', fontSize: 10, fontWeight: 700 }}>PAR</td>
              {holeRange.map(i => <td key={i} style={s.holeCol}>{holePars[i] || '-'}</td>)}
              <td style={s.totalCol}>{view === 'front' ? outPar : inPar}</td>
              <td style={s.totalCol}>{totalPar}</td>
              <td style={s.totalCol}>-</td>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map(pid => {
              const prof = profiles[pid]
              const segTotal = getTotal(pid, holeRange[0], holeRange[holeRange.length - 1] + 1)
              const fullTotal = getTotal(pid, 0, numHoles)
              const totalVsPar = fullTotal > 0 ? fullTotal - totalPar : null
              const isMe = pid === highlightPlayer
              const tag = statusTag(pid)
              const canEdit = isMe && (getStatus(pid) === 'review' || getStatus(pid) === 'live' || getStatus(pid) == null) && !allAgreed
              const hcpBadge = getHcpBadge(pid)
              const mp = matchPlayers?.find(p => p.player_id === pid)
              const kickVotes = mp?.kick_votes || []
              const iVotedKick = kickVotes.includes(highlightPlayer)
              const isKicked = mp?.status === 'kicked'
              const showKick = !isMe && onKick && players.length > 2

              return (
                <tr key={pid} style={{ ...s.playerRow, ...(isMe ? s.highlighted : {}) }}>
                  <td style={s.nameCol}>
                    {/* Row 1: name + actions */}
                    <div style={s.nameRow}>
                      <div style={s.nameLeft}>
                        <span style={s.playerName}>
                          {prof ? `${prof.first_name} ${prof.last_name || ''}`.trim() : '?'}
                        </span>
                        {prof?.username && <span style={s.username}> @{prof.username}</span>}
                        {isMe && <span style={s.youTag}> you</span>}
                      </div>
                      <div style={s.nameRight}>
                        {tag && <span style={{ ...s.statusTag, color: tag.color }}>{tag.label}</span>}
                        {canEdit && onEdit && <button style={s.editBtn} onClick={onEdit}>SCORE</button>}
                        {showKick && !isKicked && (
                          <button style={{ ...s.kickBtn, opacity: iVotedKick ? 0.5 : 1 }} onClick={() => onKick(pid)} disabled={iVotedKick}>
                            {iVotedKick ? '✓' : '⚑'}
                          </button>
                        )}
                        {isKicked && <span style={s.kickedTag}>Removed</span>}
                      </div>
                    </div>
                    {/* Row 2: handicap badge */}
                    {hcpBadge ? (
                      <div style={{ ...s.hcpBadge, color: hcpBadge.color, background: hcpBadge.color + '18', border: `1px solid ${hcpBadge.color}55` }}>
                        HCP {hcpBadge.hcp} · {hcpBadge.label}
                      </div>
                    ) : (
                      <div style={s.hcpBadgeEmpty}>No HCP</div>
                    )}
                  </td>
                  {holeRange.map(i => {
                    const score = getScore(pid, i)
                    const par = holePars[i] || 4
                    const vp = score != null ? score - par : null
                    return (
                      <td key={i} style={s.holeCol}>
                        {score != null
                          ? <div style={{ ...s.scoreCell, color: VS_PAR_COLOR(vp), fontWeight: vp !== 0 ? 700 : 400 }}>{score}</div>
                          : <div style={s.emptyCell}>-</div>}
                      </td>
                    )
                  })}
                  <td style={s.totalCol}><div style={s.totalCell}>{segTotal > 0 ? segTotal : '-'}</div></td>
                  <td style={s.totalCol}><div style={s.totalCell}>{fullTotal > 0 ? fullTotal : '-'}</div></td>
                  <td style={s.totalCol}>
                    {totalVsPar != null && fullTotal > 0
                      ? <div style={{ ...s.vpCell, color: VS_PAR_COLOR(totalVsPar) }}>
                          {totalVsPar > 0 ? '+' + totalVsPar : totalVsPar === 0 ? 'E' : totalVsPar}
                        </div>
                      : <div style={s.emptyCell}>-</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  toggle: { display: 'flex', gap: 6 },
  toggleBtn: { flex: 1, padding: '8px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  toggleActive: { background: '#0A0A0A', border: '1.5px solid #0A0A0A', color: '#FDF9E7' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: "'DM Sans', sans-serif", fontSize: 13 },
  nameCol: { textAlign: 'left', padding: '6px 8px 6px 0', minWidth: 110, whiteSpace: 'nowrap' },
  holeCol: { textAlign: 'center', padding: '4px 3px', minWidth: 26, fontSize: 12, color: '#7A7570', fontWeight: 600 },
  totalCol: { textAlign: 'center', padding: '4px 6px', minWidth: 32, fontSize: 12, fontWeight: 700, color: '#1A1814' },
  parRowStyle: { background: '#FDF9E7' },
  playerRow: { borderTop: '1px solid #EDE8D8' },
  highlighted: { background: 'rgba(0,96,57,0.04)' },
  nameRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 },
  nameLeft: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 },
  nameRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  playerName: { fontSize: 13, fontWeight: 700, color: '#1A1814' },
  username: { fontSize: 10, color: '#7A7570', fontWeight: 400 },
  youTag: { fontSize: 10, color: '#00563F', fontWeight: 700 },
  statusTag: { fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' },
  editBtn: { fontSize: 10, fontWeight: 700, color: '#00563F', background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.3)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  kickBtn: { fontSize: 10, fontWeight: 700, color: '#C19A6B', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit' },
  kickedTag: { fontSize: 9, fontWeight: 700, color: '#7A7570', background: '#EDE8D8', borderRadius: 6, padding: '2px 6px' },
  hcpBadge: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', marginTop: 3, display: 'inline-block' },
  hcpBadgeEmpty: { fontSize: 10, color: '#C0C0C0', fontStyle: 'italic', marginTop: 2 },
  scoreCell: { textAlign: 'center', fontSize: 13 },
  emptyCell: { textAlign: 'center', color: '#D0CCC0', fontSize: 12 },
  totalCell: { textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: '#1A1814' },
  vpCell: { textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, fontWeight: 700 },
}
